/**
 * Immutable prediction generation log (spec 7.3).
 *
 * A prediction is logged once per baby/type/basis event/model version; later
 * calls with the same identity keep the first generated window. Resolution
 * fills only the observation fields, once, when the next event is recorded.
 * Logs are local-only and are written only for the active local household.
 */
import { db, type PredictionLog, type PredictionType } from '@/db/db';
import { getHousehold } from '@/db/repo';
import type { LocalEvent } from '@/lib/schemas';
import { nowIso } from '@/lib/ids';
import { MINUTE_MS } from './common';
import { DEFAULT_FEEDING_OPTIONS, feedSessions, type FeedingPrediction } from './feeding';
import type { SleepPrediction } from './sleep';

export function predictionLogId(babyId: string, type: PredictionType, basisEventId: string, modelVersion: string): string {
  return `${babyId}|${type}|${basisEventId}|${modelVersion}`;
}

type Loggable =
  | Extract<FeedingPrediction, { status: 'ok' }>
  | Extract<SleepPrediction, { status: 'awake' | 'sleeping' }>;

function typeOf(p: Loggable): PredictionType {
  if (!('experimental' in p)) return 'feed';
  return p.status === 'awake' ? 'sleep_start' : 'wake';
}

/** Build the log row for a prediction generated at `generatedMs` (pure). */
export function buildPredictionLog(babyId: string, prediction: Loggable, generatedMs: number): PredictionLog {
  const ms = Math.floor(generatedMs);
  const type = typeOf(prediction);
  return {
    id: predictionLogId(babyId, type, prediction.basisEventId, prediction.modelVersion),
    baby_id: babyId,
    prediction_type: type,
    model_version: prediction.modelVersion,
    generated_at: nowIso(ms),
    generated_ms: ms,
    basis_event_id: prediction.basisEventId,
    sample_size: prediction.sampleSize,
    window_start: nowIso(prediction.window.startMs),
    window_end: nowIso(prediction.window.endMs),
    point: nowIso(prediction.window.pointMs),
    observed_at: null,
    error_minutes: null,
    within_window: null,
  };
}

/**
 * Log a prediction unless one with the same identity already exists.
 * Returns the stored row (the original one if it was already logged), or
 * null when the baby is not in the active local household.
 */
export async function logPrediction(babyId: string, prediction: Loggable, generatedMs = Date.now()): Promise<PredictionLog | null> {
  const row = buildPredictionLog(babyId, prediction, generatedMs);
  return db.transaction('rw', [db.predictionLog, db.babies, db.meta], async () => {
    const household = await getHousehold();
    const baby = await db.babies.get(babyId);
    if (!household || household.cloud || !baby || baby.deleted_at || baby.household_id !== household.id) return null;
    const existing = await db.predictionLog.get(row.id);
    if (existing) return existing;
    await db.predictionLog.add(row);
    return row;
  });
}

/** When the predicted event actually happened, if it has been recorded yet. */
export function observedMsFor(log: PredictionLog, events: LocalEvent[]): number | null {
  const live = events.filter((e) => !e.deleted_at && e.baby_id === log.baby_id);
  const basis = live.find((e) => e.id === log.basis_event_id);
  if (!basis) return null;
  switch (log.prediction_type) {
    case 'feed': {
      // The next feeding session after the basis session, using the model's merge rule.
      const sessions = feedSessions(live, DEFAULT_FEEDING_OPTIONS.mergeWithinMin);
      const i = sessions.findIndex((s) => s.id === basis.id);
      return i >= 0 && i + 1 < sessions.length ? sessions[i + 1].startMs : null;
    }
    case 'sleep_start': {
      if (basis.ended_ms === null) return null;
      const next = live
        .filter((e) => e.type === 'sleep' && e.id !== basis.id && e.started_ms >= basis.ended_ms!)
        .sort((a, b) => a.started_ms - b.started_ms)[0];
      return next?.started_ms ?? null;
    }
    case 'wake':
      return basis.ended_ms;
  }
}

/**
 * Fill the observation fields of unresolved logs for a baby. Prediction
 * fields are never rewritten and resolved observations are never changed.
 * Returns the number of logs resolved.
 */
export async function resolvePredictionLogs(babyId: string, events: LocalEvent[]): Promise<number> {
  return db.transaction('rw', db.predictionLog, async () => {
    const pending = await db.predictionLog
      .where('[baby_id+prediction_type+generated_ms]')
      .between([babyId, '', -Infinity], [babyId, '￿', Infinity])
      .filter((l) => l.observed_at === null)
      .toArray();
    let resolved = 0;
    for (const log of pending) {
      const observed = observedMsFor(log, events);
      if (observed === null) continue;
      const point = Date.parse(log.point);
      await db.predictionLog.update(log.id, {
        observed_at: nowIso(observed),
        error_minutes: (observed - point) / MINUTE_MS,
        within_window: observed >= Date.parse(log.window_start) && observed <= Date.parse(log.window_end),
      });
      resolved++;
    }
    return resolved;
  });
}

/** Live accuracy of resolved logs (signed error is observed minus point). */
export function summarizeLogs(logs: PredictionLog[]): { resolved: number; maeMin: number | null; coverage: number | null } {
  const done = logs.filter((l) => l.observed_at !== null && l.error_minutes !== null);
  if (!done.length) return { resolved: 0, maeMin: null, coverage: null };
  return {
    resolved: done.length,
    maeMin: done.reduce((s, l) => s + Math.abs(l.error_minutes!), 0) / done.length,
    coverage: done.filter((l) => l.within_window).length / done.length,
  };
}
